/**
 * Admin Topic Reviews Dashboard
 *
 * Provides analytics and management of topic reviews/feedback from students.
 * Features:
 * - Overview statistics (total reviews, average rating, sentiment breakdown)
 * - Course-specific analytics
 * - Recent reviews list
 * - Lowest/highest rated topics
 */
import React, { useState, useEffect } from 'react';
import axios, { AxiosInstance } from 'axios';
import { ThumbsUp, ThumbsDown, Minus, Star, MessageSquare, TrendingUp, TrendingDown, BarChart3 } from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import { getConfig } from '@/config/Constants';

let apiInstance: AxiosInstance | null = null;

function getApiInstance() {
    if (!apiInstance) {
        const config = getConfig();
        apiInstance = axios.create({
            baseURL: `${config.apiUrl}`,
            headers: {
                'Content-Type': 'application/json',
            },
        });

        apiInstance.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('access_token');
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );
    }
    return apiInstance;
}

interface SentimentCount {
    positive: number;
    neutral: number;
    negative: number;
}

interface TopicReviewSummary {
    topic_id: string;
    total_reviews: number;
    average_rating: number | null;
    sentiment_breakdown: SentimentCount;
}

interface CourseReviewAnalytics {
    course_id: string;
    total_reviews: number;
    average_rating: number | null;
    sentiment_breakdown: SentimentCount;
    reviews_by_module: Record<string, number>;
    lowest_rated_topics: TopicReviewSummary[];
    highest_rated_topics: TopicReviewSummary[];
    common_improvement_themes: string[];
    common_praise_themes: string[];
}

interface TopicReviewResponse {
    id: string;
    user_id: string;
    enrollment_id: string;
    course_id: string;
    topic_id: string;
    segment_id: string;
    lesson_id: string;
    module_id: string;
    sentiment: 'positive' | 'neutral' | 'negative';
    star_rating: number | null;
    liked_most: string | null;
    could_improve: string | null;
    want_more_of: string | null;
    additional_comments: string | null;
    is_partial: boolean;
    submitted_at: string | null;
    created_at: string;
    updated_at: string;
}

interface Course {
    id: string;
    title: string;
}

export default function AdminReviews() {
    const [courses, setCourses] = useState<Course[]>([]);
    const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
    const [analytics, setAnalytics] = useState<CourseReviewAnalytics | null>(null);
    const [recentReviews, setRecentReviews] = useState<TopicReviewResponse[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Fetch courses on mount
    useEffect(() => {
        const fetchCourses = async () => {
            try {
                const response = await getApiInstance().get('/courses/');
                const coursesData = response.data.data || response.data || [];
                setCourses(coursesData);
                if (coursesData.length > 0) {
                    setSelectedCourseId(coursesData[0].id);
                }
            } catch (err) {
                console.error('Error fetching courses:', err);
            }
        };
        fetchCourses();
    }, []);

    // Fetch analytics when course changes
    useEffect(() => {
        const fetchAnalytics = async () => {
            if (!selectedCourseId) return;

            setLoading(true);
            setError(null);

            try {
                const [analyticsRes, recentRes] = await Promise.all([
                    getApiInstance().get(`/admin/topic-reviews/analytics/${selectedCourseId}`),
                    getApiInstance().get(`/admin/topic-reviews/recent?course_id=${selectedCourseId}&per_page=10`)
                ]);

                setAnalytics(analyticsRes.data.data || null);
                setRecentReviews(recentRes.data.data?.reviews || []);
            } catch (err: any) {
                console.error('Error fetching analytics:', err);
                setError(err.response?.data?.detail || 'Failed to load analytics');
            } finally {
                setLoading(false);
            }
        };

        fetchAnalytics();
    }, [selectedCourseId]);

    const getSentimentIcon = (sentiment: string) => {
        switch (sentiment) {
            case 'positive':
                return <ThumbsUp className="w-4 h-4 text-green-400" />;
            case 'negative':
                return <ThumbsDown className="w-4 h-4 text-red-400" />;
            default:
                return <Minus className="w-4 h-4 text-yellow-400" />;
        }
    };

    const getSentimentColor = (sentiment: string) => {
        switch (sentiment) {
            case 'positive':
                return 'bg-green-500/20 border-green-500/50 text-green-400';
            case 'negative':
                return 'bg-red-500/20 border-red-500/50 text-red-400';
            default:
                return 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400';
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const renderStars = (rating: number | null) => {
        if (rating === null) return <span className="text-gray-500">No rating</span>;
        return (
            <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                        key={star}
                        className={`w-4 h-4 ${
                            star <= rating
                                ? 'text-yellow-400 fill-yellow-400'
                                : 'text-gray-600'
                        }`}
                    />
                ))}
            </div>
        );
    };

    return (
        <AdminLayout>
            <div className="py-8 px-6">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="mb-8">
                        <h1 className="text-3xl font-bold text-white mb-2">Topic Reviews</h1>
                        <p className="text-gray-400">View and analyze student feedback on course topics</p>
                    </div>

                    {/* Course Selector */}
                    <div className="mb-8">
                        <label className="block text-sm font-medium text-gray-400 mb-2">Select Course</label>
                        <select
                            value={selectedCourseId || ''}
                            onChange={(e) => setSelectedCourseId(e.target.value)}
                            className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-[300px]"
                        >
                            {courses.map((course) => (
                                <option key={course.id} value={course.id}>
                                    {course.title}
                                </option>
                            ))}
                        </select>
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center py-20">
                            <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : error ? (
                        <div className="bg-red-900/20 border border-red-500 rounded-lg p-6 text-red-400">
                            {error}
                        </div>
                    ) : analytics ? (
                        <>
                            {/* Stats Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                                {/* Total Reviews */}
                                <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <MessageSquare className="w-8 h-8 text-indigo-400" />
                                        <span className="text-3xl font-bold text-white">{analytics.total_reviews}</span>
                                    </div>
                                    <p className="text-gray-400 text-sm">Total Reviews</p>
                                </div>

                                {/* Average Rating */}
                                <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <Star className="w-8 h-8 text-yellow-400" />
                                        <span className="text-3xl font-bold text-white">
                                            {analytics.average_rating?.toFixed(1) || 'N/A'}
                                        </span>
                                    </div>
                                    <p className="text-gray-400 text-sm">Average Rating</p>
                                    {analytics.average_rating && renderStars(Math.round(analytics.average_rating))}
                                </div>

                                {/* Positive Sentiment */}
                                <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <ThumbsUp className="w-8 h-8 text-green-400" />
                                        <span className="text-3xl font-bold text-green-400">
                                            {analytics.sentiment_breakdown.positive}
                                        </span>
                                    </div>
                                    <p className="text-gray-400 text-sm">Positive Reviews</p>
                                    <div className="mt-2 h-2 bg-slate-700 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-green-500 rounded-full"
                                            style={{
                                                width: `${analytics.total_reviews > 0
                                                    ? (analytics.sentiment_breakdown.positive / analytics.total_reviews) * 100
                                                    : 0}%`
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Negative Sentiment */}
                                <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <ThumbsDown className="w-8 h-8 text-red-400" />
                                        <span className="text-3xl font-bold text-red-400">
                                            {analytics.sentiment_breakdown.negative}
                                        </span>
                                    </div>
                                    <p className="text-gray-400 text-sm">Needs Improvement</p>
                                    <div className="mt-2 h-2 bg-slate-700 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-red-500 rounded-full"
                                            style={{
                                                width: `${analytics.total_reviews > 0
                                                    ? (analytics.sentiment_breakdown.negative / analytics.total_reviews) * 100
                                                    : 0}%`
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Two Column Layout */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                                {/* Highest Rated Topics */}
                                <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-6">
                                    <div className="flex items-center gap-2 mb-6">
                                        <TrendingUp className="w-5 h-5 text-green-400" />
                                        <h2 className="text-lg font-semibold text-white">Highest Rated Topics</h2>
                                    </div>
                                    {analytics.highest_rated_topics.length > 0 ? (
                                        <div className="space-y-4">
                                            {analytics.highest_rated_topics.map((topic, index) => (
                                                <div key={topic.topic_id} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                                                    <div>
                                                        <span className="text-white font-medium">Topic {topic.topic_id}</span>
                                                        <span className="text-gray-500 text-sm ml-2">({topic.total_reviews} reviews)</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {renderStars(topic.average_rating ? Math.round(topic.average_rating) : null)}
                                                        <span className="text-green-400 font-bold">{topic.average_rating?.toFixed(1)}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-gray-500">No rated topics yet</p>
                                    )}
                                </div>

                                {/* Lowest Rated Topics */}
                                <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-6">
                                    <div className="flex items-center gap-2 mb-6">
                                        <TrendingDown className="w-5 h-5 text-red-400" />
                                        <h2 className="text-lg font-semibold text-white">Needs Attention</h2>
                                    </div>
                                    {analytics.lowest_rated_topics.length > 0 ? (
                                        <div className="space-y-4">
                                            {analytics.lowest_rated_topics.map((topic, index) => (
                                                <div key={topic.topic_id} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                                                    <div>
                                                        <span className="text-white font-medium">Topic {topic.topic_id}</span>
                                                        <span className="text-gray-500 text-sm ml-2">({topic.total_reviews} reviews)</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {renderStars(topic.average_rating ? Math.round(topic.average_rating) : null)}
                                                        <span className="text-red-400 font-bold">{topic.average_rating?.toFixed(1)}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-gray-500">No low-rated topics</p>
                                    )}
                                </div>
                            </div>

                            {/* Common Themes */}
                            {(analytics.common_praise_themes.length > 0 || analytics.common_improvement_themes.length > 0) && (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                                    {/* Praise Themes */}
                                    {analytics.common_praise_themes.length > 0 && (
                                        <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-6">
                                            <h2 className="text-lg font-semibold text-white mb-4">What Students Love</h2>
                                            <div className="flex flex-wrap gap-2">
                                                {analytics.common_praise_themes.map((theme, index) => (
                                                    <span key={index} className="px-3 py-1 bg-green-500/20 border border-green-500/50 text-green-400 rounded-full text-sm">
                                                        {theme}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Improvement Themes */}
                                    {analytics.common_improvement_themes.length > 0 && (
                                        <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-6">
                                            <h2 className="text-lg font-semibold text-white mb-4">Areas for Improvement</h2>
                                            <div className="flex flex-wrap gap-2">
                                                {analytics.common_improvement_themes.map((theme, index) => (
                                                    <span key={index} className="px-3 py-1 bg-orange-500/20 border border-orange-500/50 text-orange-400 rounded-full text-sm">
                                                        {theme}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Recent Reviews */}
                            <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-6">
                                <h2 className="text-lg font-semibold text-white mb-6">Recent Reviews</h2>
                                {recentReviews.length > 0 ? (
                                    <div className="space-y-4">
                                        {recentReviews.map((review) => (
                                            <div key={review.id} className="p-4 bg-slate-700/50 rounded-lg">
                                                <div className="flex items-start justify-between mb-3">
                                                    <div className="flex items-center gap-3">
                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getSentimentColor(review.sentiment)}`}>
                                                            <span className="flex items-center gap-1">
                                                                {getSentimentIcon(review.sentiment)}
                                                                {review.sentiment}
                                                            </span>
                                                        </span>
                                                        <span className="text-gray-400 text-sm">Topic {review.topic_id}</span>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        {review.star_rating && renderStars(review.star_rating)}
                                                        <span className="text-gray-500 text-xs">{formatDate(review.created_at)}</span>
                                                    </div>
                                                </div>

                                                {review.liked_most && (
                                                    <div className="mb-2">
                                                        <span className="text-green-400 text-xs font-medium">Liked:</span>
                                                        <p className="text-gray-300 text-sm">{review.liked_most}</p>
                                                    </div>
                                                )}

                                                {review.could_improve && (
                                                    <div className="mb-2">
                                                        <span className="text-orange-400 text-xs font-medium">Could improve:</span>
                                                        <p className="text-gray-300 text-sm">{review.could_improve}</p>
                                                    </div>
                                                )}

                                                {review.want_more_of && (
                                                    <div className="mb-2">
                                                        <span className="text-blue-400 text-xs font-medium">Want more of:</span>
                                                        <p className="text-gray-300 text-sm">{review.want_more_of}</p>
                                                    </div>
                                                )}

                                                {review.additional_comments && (
                                                    <div>
                                                        <span className="text-gray-400 text-xs font-medium">Comments:</span>
                                                        <p className="text-gray-300 text-sm">{review.additional_comments}</p>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-gray-500 text-center py-8">No reviews yet for this course</p>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-20 text-gray-500">
                            Select a course to view analytics
                        </div>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
}
